import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { Page } from 'equasion-lib';
import { explainUnusableSerialScan } from '@/lib/scanning/explainScan';
import type { ScanResult } from '@/lib/scanning/scanResult';
import { expectScanHit, playSuccessTone } from '@/lib/scanning/feedback';
import { scanAlert } from '@/lib/scanning/scanAlert';
import { scannedLotSearch } from '@/lib/scanning/scannedLot';
import { useRootStore } from '@/lib/providers/RootProvider';
import { SCAN_PROFILE } from '@/root/App/com/ScannerProvider/ScannerProvider';
import NavMain from '../../root/App/com/NavMain/NavMain';
import SharedScanner from '@/lib/shared-coms/SharedScanner';
import RecentShipments from './RecentShipments';
import useZoneScanLookup from '@/lib/shared-coms/ZoneScanner/useZoneScanLookup';
import {
  productCodeFromScan,
  scannedProductPath,
  WAREHOUSE_PRODUCT_STOCK_BY_CODE
} from '@/pages/ScannedProduct/productStock';
import {
  salesOrderIsCancelled,
  salesOrderIsOnHold,
  salesOrderNumberFromScan,
  salesOrderScreenFor,
  WAREHOUSE_SALES_ORDER_BY_NUMBER
} from './salesOrderScan';
import './Scanner.scss';

const Scanner = () => {
  const navigate = useNavigate();
  const companyId = useRootStore((state) => state.selectedCompany?.id);
  const [lookupOrder] = useLazyQuery(WAREHOUSE_SALES_ORDER_BY_NUMBER, {
    fetchPolicy: 'network-only'
  });
  const [lookupProduct] = useLazyQuery(WAREHOUSE_PRODUCT_STOCK_BY_CODE, {
    fetchPolicy: 'network-only'
  });
  const lookupZoneCode = useZoneScanLookup({ companyId: companyId as string });
  const lookupInFlight = useRef(false);

  /**
   * A picking slip's order number, a product's UPC, or — when the digits could be either — both,
   * looked up together. Order numbers are 8 digits, the shape of a UPC-E / EAN-8, and about one in
   * seven passes its check digit, so the sales order wins — including over a failed order lookup:
   * scanning a slip is what this screen has always done.
   */
  const openScannedCode = async ({
    salesOrderNumber,
    productCode
  }: {
    salesOrderNumber: string | null;
    productCode: string | null;
  }) => {
    const what = productCode ? 'barcode' : 'sales order barcode';
    const connectionAlert = () =>
      scanAlert(
        productCode ? 'Could not look up that barcode' : 'Could not look up that order',
        `Check your connection and scan the ${what} again.`,
        6,
        'warning'
      );
    if (!companyId) {
      scanAlert('No warehouse selected', `Choose a company before scanning a ${what}.`, 6, 'warning');
      return;
    }
    if (lookupInFlight.current) return;
    lookupInFlight.current = true;
    try {
      const [orderResult, productResult] = await Promise.all([
        salesOrderNumber
          ? lookupOrder({ variables: { companyId, vendorSalesOrderNumber: salesOrderNumber } })
          : null,
        productCode ? lookupProduct({ variables: { companyId, code: productCode } }) : null
      ]);
      const salesOrder = orderResult?.data?.warehouseSalesOrderByNumber;
      if (salesOrderIsOnHold(salesOrder)) {
        scanAlert(
          'Sales Order Put on Hold',
          `Sales order “${salesOrder.vendorSalesOrderNumber || salesOrderNumber}” was put on hold. It can’t be picked until it is taken off hold.`,
          6,
          'warning'
        );
        return;
      }
      if (salesOrderIsCancelled(salesOrder)) {
        scanAlert(
          'Sales Order Cancelled',
          `Sales order “${salesOrder.vendorSalesOrderNumber || salesOrderNumber}” was cancelled. It can’t be picked or shipped — set its pick ticket aside.`,
          6,
          'warning'
        );
        return;
      }
      const path = salesOrderScreenFor(salesOrder);
      if (path) {
        playSuccessTone();
        navigate(path);
        return;
      }
      // Checked before the product: an order lookup that failed has not ruled out the slip, and
      // opening whatever product shares its digits would hide that the scan went unanswered.
      for (const error of [orderResult?.error, productResult?.error]) {
        if (!error) continue;
        // GraphQL errors already toast from the Apollo error link. A dropped connection does not.
        if (!error.graphQLErrors?.length) connectionAlert();
        return;
      }
      if (productCode && productResult?.data?.warehouseProductStockByCode?.length) {
        playSuccessTone();
        navigate(scannedProductPath(productCode));
        return;
      }
      // Neither an order nor a product: a zone label opens that zone's count.
      if (salesOrderNumber) {
        const { zone } = await lookupZoneCode(salesOrderNumber);
        if (zone?.zoneType === 'stock') {
          playSuccessTone();
          navigate({ pathname: '/zone-count', search: `?zoneId=${encodeURIComponent(zone.id)}` });
          return;
        }
        if (zone) {
          scanAlert(
            `${zone.name} is a quarantine zone`,
            'Scanning a stock zone’s label opens its Zone Count. Quarantine zones are not counted there.',
            6,
            'warning'
          );
          return;
        }
      }
      if (salesOrderNumber && productCode) {
        scanAlert(
          'No matching product or sales order',
          `“${productCode}” isn’t a product in your catalog, and no sales order that is picking, shipping or already shipped uses that number.`,
          6,
          'warning'
        );
      } else if (productCode) {
        scanAlert(
          'No matching product',
          `No product in your catalog uses UPC “${productCode}”.`,
          6,
          'warning'
        );
      } else {
        scanAlert(
          'No matching sales order',
          `“${salesOrderNumber}” isn’t a product barcode or a zone label, and no sales order that is picking, shipping or already shipped uses that number.`,
          6,
          'warning'
        );
      }
    } catch {
      connectionAlert();
    } finally {
      lookupInFlight.current = false;
    }
  };

  return (
    <Page className="scanner">
      <NavMain />
      {/* The last order shipped from here, one tap from printing its labels again. */}
      <RecentShipments companyId={companyId as string} />
      <SharedScanner
        showSSCC
        scanProfile={SCAN_PROFILE.HOME}
        handleScan={(result: ScanResult) => {
          // A UPC is recognised from its digits before anything else: some parse as a GS1
          // element string (even with a bogus serial), which the item screen would refuse.
          const productCode = productCodeFromScan(result);
          // Numeric order numbers are always 8 digits (padSequence), so a longer UPC is never one.
          const salesOrderNumber =
            productCode && productCode.length !== 8 ? null : salesOrderNumberFromScan(result);
          if (productCode || salesOrderNumber) {
            void openScannedCode({ salesOrderNumber, productCode });
            return;
          }
          const { serialNumber, gtin } = result;
          // Never fail silently. On a handheld the screen simply not moving is the same
          // symptom as the scan never arriving, and the two need completely different fixes.
          const refusal = explainUnusableSerialScan(result);
          if (refusal) {
            scanAlert(refusal.title, refusal.message, 6, 'warning');
            return;
          }
          expectScanHit();
          navigate({
            pathname: '/scanned-options',
            search: `?serialNumber=${serialNumber}${gtin ? `&gtin=${gtin}` : ''}${scannedLotSearch(
              result
            )}`
          });
        }}
      />
    </Page>
  );
};

export default Scanner;
